import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";
import { EntryType } from "@/data/directoryTypes";

const allTypes: EntryType[] = [
  "Regulator", "Regional Entity", "ISAC", "Standard", "Product",
  "Consultant", "Training", "Influencer", "Event", "Procurement", "Reference",
];

const suggestSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be under 100 characters"),
  type: z.string().min(1, "Please select a type"),
  url: z.string().trim().url("Please enter a valid URL").max(500).or(z.literal("")),
  description: z.string().trim().min(1, "Description is required").max(500, "Description must be under 500 characters"),
});

interface SuggestEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SuggestEntryDialog = ({ open, onOpenChange }: SuggestEntryDialogProps) => {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setName("");
    setType("");
    setUrl("");
    setDescription("");
    setErrors({});
  };

  const handleSubmit = () => {
    const result = suggestSchema.safeParse({ name, type, url, description });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((e) => {
        const field = e.path[0] as string;
        if (!fieldErrors[field]) fieldErrors[field] = e.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    const subject = encodeURIComponent(`NERC CIP Directory Suggestion: ${name}`);
    const body = encodeURIComponent(
      `New Directory Entry Suggestion\n\nName: ${name}\nType: ${type}\nWebsite/LinkedIn: ${url || "Not provided"}\n\nWhy it belongs:\n${description}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_self");

    toast({ title: "Thank you!", description: "Your email client should open with the suggestion details." });
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetForm(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send size={18} className="text-primary" />
            Suggest an Entry
          </DialogTitle>
          <DialogDescription>
            Know an organization, product, or person that should be listed? Let us know.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="suggest-name">Name *</Label>
            <Input
              id="suggest-name"
              placeholder="e.g. Acme Compliance Corp"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }}
              maxLength={100}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="suggest-type">Type *</Label>
            <Select value={type} onValueChange={(v) => { setType(v); setErrors((p) => ({ ...p, type: "" })); }}>
              <SelectTrigger id="suggest-type">
                <SelectValue placeholder="Select entry type" />
              </SelectTrigger>
              <SelectContent>
                {allTypes.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && <p className="text-xs text-destructive">{errors.type}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="suggest-url">Website or LinkedIn URL</Label>
            <Input
              id="suggest-url"
              placeholder="https://..."
              value={url}
              onChange={(e) => { setUrl(e.target.value); setErrors((p) => ({ ...p, url: "" })); }}
              maxLength={500}
            />
            {errors.url && <p className="text-xs text-destructive">{errors.url}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="suggest-desc">Why does it belong? *</Label>
            <Textarea
              id="suggest-desc"
              placeholder="Briefly describe what they do and why they're relevant to NERC CIP..."
              value={description}
              onChange={(e) => { setDescription(e.target.value); setErrors((p) => ({ ...p, description: "" })); }}
              maxLength={500}
              rows={3}
            />
            <div className="flex justify-between">
              {errors.description ? <p className="text-xs text-destructive">{errors.description}</p> : <span />}
              <span className="text-[11px] text-muted-foreground">{description.length}/500</span>
            </div>
          </div>

          <Button onClick={handleSubmit} className="w-full">
            <Send size={16} className="mr-2" />
            Submit Suggestion
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestEntryDialog;
